import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
	Container,
	Paper,
	Typography,
	Grid,
	Button,
	TextField,
	InputAdornment,
	IconButton,
	Box,
	Divider,
	Tooltip,
  FormHelperText 
} from "@material-ui/core";
import { 
	Visibility, 
	VisibilityOff, 
	Autorenew, 
	FileCopy, 
	Save as SaveIcon,
	Business as BusinessIcon,
	VpnKey as TokenIcon
} from "@material-ui/icons";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import LogoUploader from "../../components/LogoUploader";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { useTitle } from "../../hooks/useTitle";
import LocationSelector from "../../components/LocationSelector";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(4),
		paddingBottom: theme.spacing(4),
		flex: 1, 
		overflowY: "auto",
		height: "100%",
		...theme.scrollbarStyles,
	},
	sectionPaper: {
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.background.paper,
		boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
	},
	sectionHeader: {
		display: "flex",
		alignItems: "center",
		marginBottom: theme.spacing(2),
		gap: theme.spacing(1),
		color: theme.palette.text.primary,
	},
	logoContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		flexDirection: "column",
		borderLeft: "1px solid " + theme.palette.divider,
		paddingLeft: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			borderLeft: "none",
			paddingLeft: 0,
			marginTop: theme.spacing(2),
			borderTop: "1px solid " + theme.palette.divider,
			paddingTop: theme.spacing(2),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		alignSelf: "flex-start",
	},
	tokenField: {
		backgroundColor: theme.palette.type === "dark" ? theme.palette.action.hover : "#f5f5f5",
		borderRadius: theme.shape.borderRadius,
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: "transparent",
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.divider,
		},
	},
}));

const CompanySchema = Yup.object().shape({
	name: Yup.string().min(2).max(100).required("Requerido"),
	country: Yup.string().required("Requerido"),
	state: Yup.string().required("Requerido"),
	city: Yup.string().required("Requerido"),
	zipCode: Yup.string().required("Requerido"),
	street: Yup.string().required("Requerido"),
	rfc: Yup.string().required("Requerido"),
	phone: Yup.string().required("Requerido"),
	whatsappNumber: Yup.string().required("Requerido"),
	latitude: Yup.string().required("Debe seleccionar una ubicación en el mapa"),
	longitude: Yup.string().required("Debe seleccionar una ubicación en el mapa"),
});

const Company = () => {
	useTitle("Configuración de Compañía");
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	
	const [initialValues, setInitialValues] = useState(null);
	const [showToken, setShowToken] = useState(false);
	const [apiToken, setApiToken] = useState(null);
	const [openModal, setOpenModal] = useState(false);

	useEffect(() => {
		const fetchCompany = async () => {
			try {
				const { data } = await api.get("/companies/" + user.companyId);
				setInitialValues({
					name: data.name || "",
					country: data.country || "",
					state: data.state || "",
					city: data.city || "",
					zipCode: data.zipCode || "",
					street: data.street || "",
					rfc: data.rfc || "",
					phone: data.phone || "",
					whatsappNumber: data.whatsappNumber || "",
					latitude: data.latitude || "",
					longitude: data.longitude || "",
					logo: null,
					logoUrl: data.logoUrl || null,
				});
				setApiToken(data.apiToken);
			} catch (err) {
				toastError(err);
			}
		};
		if (user.companyId) {
			fetchCompany();
		}
	}, [user]);

	const handleRegenerateToken = async () => {
		try {
			const { data } = await api.put("/companies/" + user.companyId + "/regenerate-token");
			setApiToken(data.apiToken);
			toast.success("API Token regenerado correctamente");
			setOpenModal(false);
		} catch (err) {
			toastError(err);
		}
	};

	const handleCopy = async () => {
		if (!apiToken) return;
		try {
			if (navigator.clipboard && navigator.clipboard.writeText) {
				await navigator.clipboard.writeText(apiToken);
			} else {
				const textArea = document.createElement("textarea");
				textArea.value = apiToken;
				document.body.appendChild(textArea);
				textArea.select();
				document.execCommand("copy");
				document.body.removeChild(textArea);
			}
			toast.success("Token copiado al portapapeles");
		} catch (err) {
			console.error(err);
			toast.error("Error al copiar");
		}
	};

	const handleSave = async (values) => {
		try {
			const formData = new FormData();
			Object.keys(values).forEach((key) => {
				if (key === "logo") {
					if (values.logo instanceof File) formData.append("logo", values.logo);
				} else if (key !== "logoUrl") {
					formData.append(key, values[key]);
				}
			});

			await api.put("/companies/" + user.companyId, formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			toast.success(i18n.t("company.updated"));
		} catch (err) {
			toastError(err);
		}
	};

	if (!initialValues) return null;

	return (
		<MainContainer>
			<MainHeader>
				<Title>{i18n.t("company.title")}</Title>
				<MainHeaderButtonsWrapper />
			</MainHeader>

			<Container maxWidth="md" className={classes.root}>
				
				<Paper className={classes.sectionPaper} variant="outlined">
					<div className={classes.sectionHeader}>
						<BusinessIcon color="primary" />
						<Typography variant="h6" style={{ fontWeight: 600 }}>
							Información de la Compañía
						</Typography>
					</div>
					
					<Formik
						initialValues={initialValues}
						enableReinitialize
						validationSchema={CompanySchema}
						onSubmit={(values, actions) => {
							handleSave(values);
							actions.setSubmitting(false);
						}}
					>
						{({ touched, errors, setFieldValue, isSubmitting, values }) => (
							<Form>
								<Grid container spacing={3}>
									<Grid item xs={12} sm={8}>
										<Grid container spacing={2}>
											<Grid item xs={12}>
												<Field
													as={TextField}
													name="name"
													label="Nombre de la Compañía"
													variant="outlined"
													fullWidth
													error={touched.name && Boolean(errors.name)}
													helperText={touched.name && errors.name}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<Field
													as={TextField}
													name="rfc"
													label="RFC / ID Fiscal"
													variant="outlined"
													fullWidth
													error={touched.rfc && Boolean(errors.rfc)}
													helperText={touched.rfc && errors.rfc}
												/>
											</Grid>
											<Grid item xs={12} sm={6}>
												<Field
													as={TextField}
													name="phone"
													label="Teléfono"
													variant="outlined"
													fullWidth
													error={touched.phone && Boolean(errors.phone)}
													helperText={touched.phone && errors.phone}
												/>
											</Grid>
											<Grid item xs={12}>
												<Field
													as={TextField}
													name="whatsappNumber"
													label="WhatsApp Business"
													variant="outlined"
													fullWidth
													error={touched.whatsappNumber && Boolean(errors.whatsappNumber)}
													helperText={touched.whatsappNumber && errors.whatsappNumber}
												/>
											</Grid>
										</Grid>
									</Grid>

									<Grid item xs={12} sm={4}>
										<div className={classes.logoContainer}>
											<Typography variant="subtitle2" gutterBottom color="textSecondary">
												Logotipo
											</Typography>
											<LogoUploader 
												onChange={(file) => setFieldValue("logo", file)}
												initialLogoUrl={values.logoUrl}
											/>
											{touched.logo && errors.logo && (
												<Typography color="error" variant="caption">
													{errors.logo}
												</Typography>
											)}
										</div>
									</Grid>

									<Grid item xs={12}>
										<Divider style={{ margin: "8px 0 16px" }} />
										<Typography variant="subtitle2" gutterBottom color="textSecondary">
											Dirección y Ubicación
										</Typography>
									</Grid>

									<Grid item xs={12} sm={6}>
										<Field as={TextField} name="street" label="Calle y Número" variant="outlined" fullWidth />
									</Grid>
									<Grid item xs={12} sm={6}>
										<Field as={TextField} name="zipCode" label="Código Postal" variant="outlined" fullWidth />
									</Grid>
									<Grid item xs={12} sm={4}>
										<Field as={TextField} name="city" label="Ciudad" variant="outlined" fullWidth />
									</Grid>
									<Grid item xs={12} sm={4}>
										<Field as={TextField} name="state" label="Estado / Provincia" variant="outlined" fullWidth />
									</Grid>
									<Grid item xs={12} sm={4}>
										<Field as={TextField} name="country" label="País" variant="outlined" fullWidth />
									</Grid>
									
									<Grid item xs={12}>
										<LocationSelector 
											country={values.country}
											state={values.state}
											city={values.city}
											street={values.street}
											initialLat={values.latitude}
											initialLng={values.longitude}
											onLocationChange={(lat, lng) => {
												setFieldValue("latitude", lat);
												setFieldValue("longitude", lng);
											}}
										/>
                    {touched.latitude && errors.latitude && (
											<FormHelperText error style={{ marginTop: 8, textAlign: "center" }}>
												⚠️ {errors.latitude}
											</FormHelperText>
										)}
									</Grid>
								</Grid>

								<Box display="flex" justifyContent="flex-end" mt={3}>
									<Button
										type="submit"
										variant="contained"
										color="primary"
										disabled={isSubmitting}
										startIcon={<SaveIcon />}
										size="large"
									>
										Guardar Cambios
									</Button>
								</Box>
							</Form>
						)}
					</Formik>
				</Paper>

				<Paper className={classes.sectionPaper} variant="outlined">
					<div className={classes.sectionHeader}>
						<TokenIcon color="action" />
						<Typography variant="h6" style={{ fontWeight: 600 }}>
							Integración (API Token)
						</Typography>
					</div>
					
					<Typography variant="body2" color="textSecondary" paragraph>
						Utiliza este token para conectar aplicaciones externas a ClinicChat. Mantén este token en secreto.
					</Typography>

					<TextField
						variant="outlined"
						fullWidth
						type={showToken ? "text" : "password"}
						value={apiToken || ""}
						className={classes.tokenField}
						InputProps={{
							readOnly: true,
							endAdornment: (
								<InputAdornment position="end">
									<Tooltip title={showToken ? "Ocultar" : "Mostrar"}>
										<IconButton onClick={() => setShowToken(!showToken)}>
											{showToken ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</Tooltip>
									<Tooltip title="Copiar al portapapeles">
										<IconButton onClick={handleCopy} color="primary">
											<FileCopy />
										</IconButton>
									</Tooltip>
									<Tooltip title="Generar nuevo token">
										<IconButton onClick={() => setOpenModal(true)} color="secondary">
											<Autorenew />
										</IconButton>
									</Tooltip>
								</InputAdornment>
							),
						}}
					/>
				</Paper>

				<ConfirmationModal
					title="Regenerar API Token"
					open={openModal}
					onClose={() => setOpenModal(false)}
					onConfirm={handleRegenerateToken}
				>
					<Typography color="error" variant="body2" gutterBottom>
						⚠️ Esta acción es irreversible.
					</Typography>
					<Typography variant="body1">
						Si regeneras el token, todas las aplicaciones externas que lo estén usando dejarán de funcionar hasta que actualices el token en ellas.
						<br /><br />
						¿Estás seguro de continuar?
					</Typography>
				</ConfirmationModal>

			</Container>
		</MainContainer>
	);
};

export default Company;